import { useClickable } from "@chakra-ui/clickable";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionItemProps,
  AccordionPanel,
  AccordionProps,
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Spacer,
  Text,
  useAccordionItemState,
  useMultiStyleConfig,
  usePrevious,
} from "@chakra-ui/react";
import { StyleProps } from "@chakra-ui/styled-system";
import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AccordionIcon } from "shared/components/accordion/AccordionIcon";
import { MoreHorizontalIcon } from "shared/icons/MoreHorizontalIcon";
import { isSafari } from "shared/utils/BrowserUtils";
import { getFormFieldNames } from "shared/utils/ErrorUtils";
import { getFieldError } from "shared/utils/FormUtils";
import { stopPropagationIfActivationKey } from "shared/utils/KeyboardEventUtils";
import { hasChildOfType } from "shared/utils/ReactUtils";

interface ContentBlockGroupProps {
  defaultExpanded?: boolean;
}

export const ContentBlockGroup: React.FC<
  React.PropsWithChildren<AccordionProps> & ContentBlockGroupProps
> = ({
  children,
  allowMultiple = true,
  allowToggle = true,
  defaultExpanded = true,
  ...props
}) => (
  <Accordion
    reduceMotion={isSafari}
    variant="contentBlock"
    allowMultiple={allowMultiple}
    // If 'allowMultiple' is passed, 'allowToggle' will be ignored.
    allowToggle={allowMultiple ? undefined : allowToggle}
    defaultIndex={defaultExpanded ? [0] : []}
    sx={
      !allowToggle
        ? {
            ".chakra-accordion__button": {
              _hover: {
                cursor: "unset",
                bg: "transparent !important",
              },
            },
            ".chakra-accordion__icon": {
              visibility: "hidden",
              pointerEvents: "none",
            },
          }
        : undefined
    }
    {...props}
  >
    {children}
  </Accordion>
);

export const ContentBlock: React.FC<
  React.PropsWithChildren<AccordionItemProps>
> = ({ children, ...props }) => {
  const hasForm = Boolean(useFormContext());

  const hasHeader = hasChildOfType(children, ContentBlockHeader);

  const itemRef = useRef<HTMLDivElement>(null);

  return (
    <AccordionItem
      ref={itemRef}
      className={clsx({
        "mb-content-block": true,
        "mb-content-block__has-header": hasHeader,
        "mb-content-block__no-header": !hasHeader,
      })}
      {...props}
    >
      {!hasHeader && <AccordionButton display="none" />}
      {children}
      {hasForm && <ContentBlockFormWatcher containerRef={itemRef} />}
    </AccordionItem>
  );
};

export const ContentBlockHeader: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const hasTitle = hasChildOfType(children, ContentBlockTitle);
  const clickableProps = useClickable();
  return (
    <AccordionButton
      data-ls-enabled={true}
      data-public={true}
      as={Box}
      className={clsx({ "has-title": hasTitle })}
      gap="4px"
      {...(hasTitle
        ? {
            ...clickableProps,
            // override these so the button is not focused when clicked
            onClick: () => {},
            onMouseDown: () => {},
          }
        : { pointerEvents: "none" })}
    >
      {hasTitle && <AccordionIcon />}
      {children}
    </AccordionButton>
  );
};

export const ContentBlockTitle: React.FC<React.PropsWithChildren> = ({
  children,
}) => <Text>{children}</Text>;

export const ContentBlockBadges: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <HStack ml="8px" spacing="8px">
    {children}
  </HStack>
);

export const ContentBlockActions: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <>
    <Spacer />
    <HStack
      alignSelf="stretch"
      pointerEvents="all"
      mx="-20px"
      px="24px"
      className="mb-content-block-actions"
      justifyContent="flex-end"
      spacing="10px"
      cursor="default"
      onClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDownCapture={stopPropagationIfActivationKey}
      onKeyUpCapture={stopPropagationIfActivationKey}
    >
      {children}
    </HStack>
  </>
);

export interface ContentBlockBodyProps extends StyleProps {
  variant?: "form" | "descriptions";
}

export const ContentBlockBody: React.FC<
  React.PropsWithChildren<ContentBlockBodyProps>
> = ({ variant, children, ...props }) => {
  const styles = useMultiStyleConfig("ContentBlock", { variant });
  return (
    <AccordionPanel
      sx={{
        ...styles.body,
        ...props,
      }}
    >
      {children}
    </AccordionPanel>
  );
};

const ContentBlockFormWatcher: React.FC<{
  containerRef: React.RefObject<HTMLDivElement>;
}> = ({ containerRef }) => {
  const { onOpen, isOpen } = useAccordionItemState();

  const form = useFormContext();
  const { submitCount, errors } = useFormState();
  const prevSubmitCount = usePrevious(submitCount);
  const didSubmitCountChange = submitCount !== prevSubmitCount;

  useEffect(() => {
    const hasErroredFields = getFormFieldNames(form)
      .filter((name) => Boolean(getFieldError(errors, name)))
      .some((name) =>
        containerRef.current?.contains(document.getElementsByName(name)[0]),
      );
    if (didSubmitCountChange && hasErroredFields && !isOpen) {
      onOpen();
    }
  }, [containerRef, didSubmitCountChange, errors, form, isOpen, onOpen]);

  return null;
};

export const ContentBlockMenu: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { isOpen } = useAccordionItemState();
  const { t } = useTranslation(["common"]);
  if (!isOpen) {
    return null;
  }
  return (
    <Menu
      offset={[0, 0]}
      placement="bottom-end"
      isLazy={true}
      lazyBehavior="unmount"
    >
      <MenuButton
        as={IconButton}
        variant="ghost"
        size="none"
        w="28px"
        h="28px"
        aria-label={t("common:options")}
        icon={<MoreHorizontalIcon color="gray.400" />}
      />
      <MenuList>{children}</MenuList>
    </Menu>
  );
};
